import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useIsMobile } from "../../hooks/useIsMobile";
import { usePageTitle } from "../../hooks/usePageTitle";
import { AppHeader } from "../../components/AppHeader";
import { AppFooter } from "../../components/AppFooter";
import { apiFetch } from "../../utils/api";

export function ChangePasswordPage() {
  usePageTitle("\uBE44\uBC00\uBC88\uD638 \uBCC0\uACBD");
  const navigate = useNavigate();
  const m = useIsMobile();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const st = styles(m);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    if (newPassword !== confirmPassword) {
      setError("\uC0C8 \uBE44\uBC00\uBC88\uD638\uAC00 \uC77C\uCE58\uD558\uC9C0 \uC54A\uC2B5\uB2C8\uB2E4");
      return;
    }
    if (newPassword.length < 6) {
      setError("\uBE44\uBC00\uBC88\uD638\uB294 6\uC790 \uC774\uC0C1\uC774\uC5B4\uC57C \uD569\uB2C8\uB2E4");
      return;
    }
    if (currentPassword === newPassword) {
      setError("\uC0C8 \uBE44\uBC00\uBC88\uD638\uAC00 \uD604\uC7AC \uBE44\uBC00\uBC88\uD638\uC640 \uB3D9\uC77C\uD569\uB2C8\uB2E4");
      return;
    }
    setLoading(true);
    try {
      await apiFetch("/api/auth/change-password", {
        method: "PATCH",
        body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
      });
      setDone(true);
    } catch (err: any) {
      setError(err.message || "\uBCC0\uACBD\uC5D0 \uC2E4\uD328\uD588\uC2B5\uB2C8\uB2E4");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={st.page}>
      <AppHeader backTo="/mypage" backLabel={"\u2190 \uB9C8\uC774\uD398\uC774\uC9C0"} />
      <div style={st.container}>
        <div style={st.card}>
          <div style={st.logoArea}>
            <div style={st.icon}>{"\uD83D\uDD10"}</div>
            <h1 style={st.title}>{"\uBE44\uBC00\uBC88\uD638 \uBCC0\uACBD"}</h1>
            <div style={st.badge}>SECURITY</div>
            <p style={st.subtitle}>{"\uC0C8\uB85C\uC6B4 \uBE44\uBC00\uBC88\uD638\uB97C \uC124\uC815\uD574\uC8FC\uC138\uC694"}</p>
          </div>

          {!done ? (
            <form onSubmit={handleSubmit} style={st.form}>
              <Field
                label={"\uD604\uC7AC \uBE44\uBC00\uBC88\uD638 (\uB610\uB294 \uC784\uC2DC \uBE44\uBC00\uBC88\uD638)"}
                value={currentPassword}
                onChange={setCurrentPassword}
                placeholder={"\uD604\uC7AC \uBE44\uBC00\uBC88\uD638 \uC785\uB825"}
                type="password"
              />
              <Field
                label={"\uC0C8 \uBE44\uBC00\uBC88\uD638"}
                value={newPassword}
                onChange={setNewPassword}
                placeholder={"6\uC790 \uC774\uC0C1"}
                type="password"
              />
              <Field
                label={"\uC0C8 \uBE44\uBC00\uBC88\uD638 \uD655\uC778"}
                value={confirmPassword}
                onChange={setConfirmPassword}
                placeholder={"\uBE44\uBC00\uBC88\uD638 \uB2E4\uC2DC \uC785\uB825"}
                type="password"
              />
              {error && <p style={st.error}>{error}</p>}
              <button type="submit" disabled={loading} style={{ ...st.btn, opacity: loading ? 0.6 : 1 }}>
                {loading ? "\uBCC0\uACBD \uC911..." : "\uBE44\uBC00\uBC88\uD638 \uBCC0\uACBD"}
              </button>
            </form>
          ) : (
            <div style={st.successBox}>
              <div style={st.successIcon}>{"\u2705"}</div>
              <p style={st.successTitle}>{"\uBE44\uBC00\uBC88\uD638\uAC00 \uBCC0\uACBD\uB418\uC5C8\uC2B5\uB2C8\uB2E4"}</p>
              <p style={st.successMsg}>{"\uC0C8 \uBE44\uBC00\uBC88\uD638\uB85C \uC548\uC804\uD558\uAC8C \uB85C\uADF8\uC778\uD558\uC138\uC694."}</p>
              <button onClick={() => navigate("/dashboard")} style={st.btn}>
                {"\uB300\uC2DC\uBCF4\uB4DC\uB85C \uC774\uB3D9"}
              </button>
            </div>
          )}
        </div>
      </div>
      <AppFooter />
    </div>
  );
}

function Field({
  label, value, onChange, placeholder, type = "text",
}: {
  label: string; value: string; onChange: (v: string) => void; placeholder?: string; type?: string;
}) {
  const fieldStyle: React.CSSProperties = { display: "flex", flexDirection: "column", gap: 6 };
  const labelStyle: React.CSSProperties = { fontSize: 12, fontWeight: 600, color: "var(--text-muted)", letterSpacing: "0.5px", textTransform: "uppercase" };
  const inputStyle: React.CSSProperties = {
    padding: "13px 14px", borderRadius: "var(--radius-md)",
    border: "1px solid var(--border-medium)",
    background: "var(--bg-secondary)", color: "var(--text-primary)",
    fontSize: 16, outline: "none",
  };
  return (
    <div style={fieldStyle}>
      <label style={labelStyle}>{label}</label>
      <input type={type} placeholder={placeholder} value={value} onChange={(e) => onChange(e.target.value)} style={inputStyle} required />
    </div>
  );
}

function styles(m: boolean): Record<string, React.CSSProperties> {
  return {
    page: { minHeight: "100vh", display: "flex", flexDirection: "column" as const, background: "var(--bg-primary)" },
    container: {
      flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
      padding: m ? 16 : 20,
      background: "radial-gradient(ellipse at 50% -10%, rgba(212,168,83,0.07) 0%, transparent 65%)",
    },
    card: {
      width: "100%", maxWidth: 560,
      background: "var(--bg-card)",
      border: "1px solid var(--border-gold, rgba(212,168,83,0.2))",
      borderTop: "2px solid var(--lt-gold)",
      borderRadius: "var(--radius-xl)",
      padding: m ? "32px 20px 28px" : "44px 40px 36px",
      boxShadow: "var(--shadow-gold, 0 0 0 1px rgba(212,168,83,0.15), 0 24px 56px rgba(0,0,0,0.6))",
    },
    logoArea: { textAlign: "center" as const, marginBottom: 28 },
    icon: { fontSize: 30, marginBottom: 12 },
    badge: {
      display: "inline-block", padding: "3px 12px", borderRadius: 20,
      border: "1px solid rgba(212,168,83,0.35)", color: "var(--lt-gold)",
      fontSize: 10, fontWeight: 700, letterSpacing: "1.5px", marginBottom: 8,
    },
    title: { fontSize: m ? 26 : 30, fontWeight: 800, color: "var(--text-primary)", margin: "0 0 6px" },
    subtitle: { fontSize: 13, color: "var(--text-muted)", marginTop: 0 },
    form: { display: "flex", flexDirection: "column" as const, gap: 16 },
    error: {
      color: "var(--lt-red)", fontSize: 13, margin: 0,
      padding: "8px 12px", background: "rgba(239,68,68,0.08)", borderRadius: "var(--radius-md)",
    },
    btn: {
      padding: "14px", borderRadius: "var(--radius-md)", border: "none",
      background: "linear-gradient(135deg, #B8972E, #D4AF37)",
      color: "#0F172A", fontSize: 16, fontWeight: 700,
      cursor: "pointer", marginTop: 4, letterSpacing: "0.3px",
    },
    successBox: {
      textAlign: "center" as const,
      display: "flex", flexDirection: "column" as const, alignItems: "center", gap: 12,
      padding: "8px 0",
    },
    successIcon: { fontSize: 48 },
    successTitle: { fontSize: 18, fontWeight: 700, color: "var(--text-primary)", margin: 0 },
    successMsg: { fontSize: 14, color: "var(--text-secondary)", margin: 0 },
  };
}
